import { useEffect, useMemo, useState } from "react";
import { UseFinanceChartDataProps } from "../types";
import { CfoApi } from "@services/cfo/cfo.api";

export const useFinanceChartData = ({
  timeRange,
  asin,
  dateRange,
}: UseFinanceChartDataProps) => {
  const cfoApi = useMemo(() => new CfoApi(), []);
  const [chartData, setChartData] = useState<Record<string, number[]>>();
  const [periods, setPeriods] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    return `${day} ${month}`;
  };

  const formatPeriods = (isoPeriods: string[]): string[] => {
    return isoPeriods.map((period, index) => {
      if (index === isoPeriods.length - 1) {
        return formatDate(period);
      }

      const currentDate = formatDate(period);
      const nextDate = formatDate(isoPeriods[index + 1]);
      return `${currentDate} - ${nextDate}`;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!dateRange?.startDate || !dateRange?.endDate) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        console.log("Fetching Finance Chart Data with params:", {
          timeRange,
          dateRange,
          asin,
        });

        const startDateString = dateRange.startDate.toISOString().split("T")[0];
        const endDateString = dateRange.endDate.toISOString().split("T")[0];

        const res = await cfoApi.getCashFlowChartData({
          startDate: startDateString,
          endDate: endDateString,
          asin: asin,
          period: timeRange,
        });

        console.log("Fetched Finance Chart Data:", res);

        if (res?.metrics && res.periods) {
          setChartData(res.metrics);

          const formattedPeriods = formatPeriods(res.periods);
          setPeriods(formattedPeriods);
        } else {
          throw new Error("Invalid data format received from API");
        }
      } catch (error) {
        console.error("Error fetching finance chart data:", error);
        setError(
          error instanceof Error ? error.message : "Failed to fetch chart data",
        );

        // Generate fallback data
        generateFallbackData();
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [cfoApi, timeRange, dateRange, asin]);

  // Generate fallback data if API fails
  const generateFallbackData = () => {
    const fallbackPeriods = [
      "1 Jan - 1 Feb",
      "1 Feb - 1 Mar",
      "1 Mar - 1 Apr",
      "1 Apr - 1 May",
      "1 May",
    ];

    const fallbackData: Record<string, number[]> = {
      amazon_ad_sales: [5000, 5500, 6000, 6200, 6500],
      amazon_organic_sales: [4000, 4200, 4400, 4600, 4800],
      tiktok_sales: [2000, 2200, 2400, 2600, 2800],
      shopify_sales: [1000, 1100, 1200, 1300, 1400],
      amazon_cogs: [-3000, -3300, -3600, -3800, -4000],
      tiktok_cogs: [-1200, -1300, -1400, -1500, -1600],
      shopify_cogs: [-600, -650, -700, -750, -800],
      amazon_fees: [-1000, -1100, -1200, -1300, -1400],
      ad_spend: [-1500, -1600, -1700, -1800, -1900],
      other_fees: [-500, -550, -600, -650, -700],
      tiktok_cost: [-400, -450, -500, -550, -600],
      shopify_cost: [-300, -320, -340, -360, -380],
      cash_flow: [2000, 2200, 2400, 2600, 2800],
    };

    setPeriods(fallbackPeriods);
    setChartData(fallbackData);
  };

  return {
    chartData,
    periods,
    isLoading,
    error,
  };
};
